/**
 * Helpers for "is this CRM connected?" checks across the app.
 *
 * Source-of-truth precedence:
 *   1. `Organization.crmConnections[]` (GQL — typed view of the CrmConnections
 *      table, authoritatively written by the remi-sync webhook). Prefer this.
 *   2. `Organization.rollout.credentials[]` (JSON blob written by the older
 *      remi-api webhook). Fallback for orgs that haven't refreshed their
 *      Apollo cache yet.
 *   3. `Organization.rollout.<legacyKey>.connected` (very old frontend write
 *      shape). Final fallback for orgs that haven't reconnected since the
 *      Rollout migration.
 */

import type { CrmConnection } from '../types/organization.types';

/** Rollout app keys we care about (matches the connectors we render). */
export type RolloutAppKey =
  | 'follow-up-boss'
  | 'cinc'
  | 'cinc-pro'
  | 'brivity'
  | 'kvcore'
  | 'sierra';

/** Maps Rollout app keys to their legacy `rollout_config.<key>` property name. */
export const LEGACY_CRM_KEY_BY_APP_KEY: Record<RolloutAppKey, string> = {
  'follow-up-boss': 'fub',
  cinc: 'cinc',
  'cinc-pro': 'cinc',
  brivity: 'brivity',
  kvcore: 'kvcore',
  sierra: 'sierra',
};

export interface RolloutCredential {
  credential_id?: string;
  app_key?: string;
  status?: string;
  enabled_stages?: Array<{ id: string | number; title?: string; name?: string }>;
  metadata?: Record<string, unknown>;
  connected_at?: string;
  [key: string]: unknown;
}

type RolloutShape = {
  credentials?: RolloutCredential[];
  [legacyKey: string]: unknown;
};

const isConnectedStatus = (status: string | null | undefined) => {
  const normalized = (status ?? '').toLowerCase();
  return normalized === 'active' || normalized === 'connected';
};

/** Returns the active CrmConnection row for an app, or undefined. */
export function findCrmConnection(
  connections: CrmConnection[] | undefined | null,
  appKey: string
): CrmConnection | undefined {
  if (!Array.isArray(connections)) return undefined;
  return connections.find(
    (c) => c?.app === appKey && isConnectedStatus(c.status)
  );
}

/** Returns the connected credential entry for an app, or undefined. */
export function findRolloutCredential(
  rollout: RolloutShape | undefined | null,
  appKey: string
): RolloutCredential | undefined {
  if (!rollout || !Array.isArray(rollout.credentials)) return undefined;
  return rollout.credentials.find(
    (c) => c?.app_key === appKey && isConnectedStatus(c.status)
  );
}

export interface RolloutCheckSources {
  crmConnections?: CrmConnection[] | null;
  rollout?: RolloutShape | null;
}

/** True if the org has this CRM connected — checks all three sources. */
export function isCrmConnectedViaRollout(
  sources: RolloutCheckSources,
  appKey: string
): boolean {
  if (findCrmConnection(sources.crmConnections, appKey)) return true;
  const { rollout } = sources;
  if (findRolloutCredential(rollout, appKey)) return true;
  if (!rollout) return false;
  const legacyKey = LEGACY_CRM_KEY_BY_APP_KEY[appKey as RolloutAppKey] || appKey;
  const legacy = rollout[legacyKey];
  return Boolean(
    legacy && typeof legacy === 'object' && (legacy as { connected?: boolean }).connected
  );
}

/** True if the org has ANY CRM connected — checks all three sources. */
export function hasAnyConnectedCrm(sources: RolloutCheckSources): boolean {
  if (
    Array.isArray(sources.crmConnections) &&
    sources.crmConnections.some((c) => isConnectedStatus(c.status))
  ) {
    return true;
  }
  const { rollout } = sources;
  if (!rollout) return false;
  if (
    Array.isArray(rollout.credentials) &&
    rollout.credentials.some((c) => isConnectedStatus(c.status))
  ) {
    return true;
  }
  return Object.entries(rollout).some(
    ([key, config]) =>
      key !== 'credentials' &&
      key !== 'updated_at' &&
      config &&
      typeof config === 'object' &&
      'connected' in (config as object) &&
      (config as { connected?: boolean }).connected
  );
}
